<template>
  <div
    ref="kMultipleSelectorRef"
    class="k-multiple-selector"
    :class="{
      'on-focus': selectorActive,
      'has-item': itemList.length > 0,
      'has-error': hasError,
      'disabled': isDisabled
    }"
    @click="openSelectorOption">
    <!-- 数据区域 -->
    <div class="items-input">
      <!-- 已输入项 -->
      <div class="items">
        <div
          class="item k-body-md"
          :class="{'no-delete': isDisabled, 'item-disabled': isDisabled}"
          v-for="(item, index) in itemList" :key="index"
        >
          <span class="text">{{ getSelectedLabel(item) }}</span>
          <span
            v-if="!isDisabled"
            @click.stop="doDeleteItem(item)"
          >
            <KkIcon name="ic_cross_line" size="xs" class="kk-u-text-primary-medium k-icon-cursor" />
          </span>
        </div>
      </div>
      <!-- 实时筛选 -->
      <div class="filter-input" v-if="matchError || (openFilter && selectorActive) || itemList.length <= 0">
        <input
          ref="filterInputRef"
          type="text"
          class="multiple-input"
          v-model="input"
          :readonly="!openFilter"
          :disabled="isDisabled"
          :placeholder="openFilter && selectorActive ? filterPlaceholder : placeholder"
        >
      </div>
      <!-- Select 箭头 -->
      <div
        class="select-icon"
        :class="{'select-icon-active': selectorActive}"
        @click.stop="changeSelectorOption">
        <KkIcon name="ic_arrowDown_line" size="sm" />
      </div>
    </div>
    <!-- 选项 -->
    <div class="items-option" :class="{'items-option-empty': optionList.length <= 0}" v-if="selectorActive" :style="{top: kMultipleSelectorHeight + 'px'}">
      <KkDropdown ref="KkDropdownRef">
        <KkSelectOption
          v-for="(item, index) in optionList"
          :key="index"
          :value="item?.value"
          :is-disabled="item?.disabled"
          @click="optionSelect(item)"
        >
          <div v-if="item?.value" class="option" :class="{'selected': checkOptionSelected(item.value)}">
            <div class="text-label">{{ item.label ? item.label : item.value }}</div>
            <div v-if="checkOptionSelected(item.value)"><KkIcon name="ic_check_line" size="sm" color="#068B9F"/></div>
          </div>
        </KkSelectOption>
        <div class="filter-empty k-body-md" v-if="optionList.length <= 0">
          {{ filterEmptyMsg }}
        </div>
      </KkDropdown>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { KkDropdown, KkSelectOption, KkIcon } from '@kkday/web-design-system'

export default {
  components: {
    KkDropdown,
    KkSelectOption,
    KkIcon
  },
  data() {
    return {
      selectorActive: false,
      kMultipleSelectorHeight: 0,
      input: '',
      itemList: this.value || [], // ['1', '2', '3']
      optionList: this.options || [], // [{value: "1",label: "label", disable: true/false}]
      noMatchList: [],
      matchError: '',
      filterEmptyMsg: this.$t('common.multipleSelector.filterEmpty')
    }
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: () => []
    },
    max: {
      type: Number,
      default: 0
    },
    openFilter: {
      type: Boolean,
      default: true
    },
    filterPlaceholder: {
      type: String,
      default: ''
    },
    filterEmpty: {
      type: String,
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    if(this.filterEmpty) this.filterEmptyMsg = this.filterEmpty
    // 元素以外点击
    const hideClickListener = (e) => {
      if (this.$refs.kMultipleSelectorRef) {
        const isSelf = this.$refs.kMultipleSelectorRef.contains(e.target)
        if (!isSelf) {
          this.selectorActive = false
          if(this.hasError == false) this.input = '' // 有报错提示的时候，就不清空
        }
      }
    }
    document.addEventListener('click', hideClickListener)
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('click', hideClickListener)
    })

    // 元素高度监听
    const resizeHeightListener = () => {
      this.resizeMultipleSelectorHeight()
    }
    window.addEventListener('resize', resizeHeightListener)
    this.$once('hook:beforeDestroy', () => {
      window.removeEventListener('resize', resizeHeightListener)
    })
  },
  watch: {
    selectorActive() {
      // 开关后，元素高度会出现变化
      this.resizeMultipleSelectorHeight()
    },
    input: {
      handler(val) {
        const optionList = _.cloneDeep(this.options)
        this.noMatchList = [] // 没有匹配到的列表
        let isMatch = false
        if (val) {
          //支持英文逗号、中文逗号、空格分隔多关键词搜索
          const lowerVal = val.toLocaleLowerCase().replaceAll(' ', ',').replaceAll('，', ',')
          const originalVal = val.replaceAll(' ', ',').replaceAll('，', ',')
          if (lowerVal.includes(',')) {
            // 多关键词搜索
            let lowerVals = lowerVal.split(',').filter(e => e != '') //过滤掉多余的空格
            let noMatchList = originalVal.split(',').filter(e => e != '')
            this.optionList = optionList.filter(option => {
              const lowerValue = option.value.toLocaleLowerCase()
              const lowerLabel = option.label.toLocaleLowerCase()
              return lowerVals.find(v => {
                if (lowerValue == v || lowerLabel == v) {
                  // 有匹配结果时
                  // 检查数量是否超限
                  if (this.max > 0 && this.itemList.length >= this.max) {
                    this.onErrorCallback('max')
                    return
                  }
                  // 直接选中
                  if (!this.checkOptionSelected(option.value)) {
                    this.itemList.push(option.value)
                  }
                  isMatch = true
                  let index = noMatchList.findIndex(item => item == v)
                  if (index != -1) noMatchList.splice(index, 1)
                }
                return lowerValue.includes(v) || lowerLabel.includes(v)
              })
            })
            if (isMatch) {
              // 匹配到选项
              this.input = noMatchList.join(' ') // 将未匹配的显示到输入框
              if (noMatchList.length > 0) this.input += ' ' // 有未匹配的时候，末尾多留一个空格
              this.onChangeCallback()
              this.resizeMultipleSelectorHeight()
            }
            if (noMatchList.length > 0) {
              // 未匹配错误提示
              this.matchError = this.$t('common.multipleSelector.matchError', {
                keyWords: noMatchList.join(' ')
              })
            } else {
              this.matchError = null
            }
          } else {
            // 单关键词搜索
            this.optionList = optionList.filter(option => {
              const lowerValue = option.value.toLocaleLowerCase()
              const lowerLabel = option.label.toLocaleLowerCase()
              return lowerValue.includes(lowerVal) || lowerLabel.includes(lowerVal)
            })
            this.matchError = null
          }
        } else {
          this.optionList = optionList
          this.matchError = null
        }
        // 将选中的排序在前
        this.optionList.forEach(e => e.checked = this.checkOptionSelected(e.value))
        this.optionList = _.orderBy(this.optionList, ['checked'], ['desc'])
        // 未匹配错误提示
        this.onMatchErrorCallback(this.matchError)
      }
    },
    value: {
      handler(val) {
        this.itemList = val
      },
      deep: true,
      immediate: true
    },
    options: {
      handler(val) {
        this.optionList = _.cloneDeep(val)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    resizeMultipleSelectorHeight() {
      this.$nextTick(() => {
        this.kMultipleSelectorHeight = this.$refs.kMultipleSelectorRef.offsetHeight + 4
      })
    },
    openSelectorOption() {
      if (this.isDisabled) return
      if (!this.selectorActive) {
        this.selectorActive = true
      }
      this.setFilterInputFocus()
    },
    changeSelectorOption() {
      if (this.isDisabled) return
      this.selectorActive = !this.selectorActive
      if (this.selectorActive) {
        this.openSelectorOption()
      }
    },
    setFilterInputFocus() {
      this.$nextTick(() => {
        if (this.openFilter) {
          this.$refs.filterInputRef.focus()
        }
      })
    },
    optionSelect(item) {
      // 数量是否超限
      if (this.max > 0 && this.itemList.length >= this.max) {
        this.onErrorCallback('max')
        return
      }
      if (!this.checkOptionSelected(item.value)) {
        this.itemList.push(item.value)
        this.onChangeCallback()
        this.resizeMultipleSelectorHeight()
        this.$emit('clickSelected', item)
      } else {
        this.doDeleteItem(item.value)
      }
    },
    checkOptionSelected(value) {
      if (_.findIndex(this.itemList, function(item) {
        return item.toString() == value.toString()
      }) > -1) {
        return true
      }
      return false
    },
    getSelectedLabel(value) {
      const find = _.find(this.options, ['value', value.toString()])
      if (!_.isEmpty(find) && find != undefined) {
        return _.get(find, 'label')
      }
      return value
    },
    doDeleteItem(value) {
      this.itemList = this.itemList.filter(item => item != value)
      this.resizeMultipleSelectorHeight()
      this.onChangeCallback()
    },
    onChangeCallback() {
      this.$emit('input', this.itemList)
      this.$emit('change', this.itemList)
    },
    onErrorCallback(error) {
      this.$emit('error', error)
    },
    onMatchErrorCallback(error) {
      this.$emit('match-error', error)
    }
  }
}
</script>

<style lang="less" scoped>
  // 边框状态：正常状态、焦点状态、鼠标悬停
  .k-multiple-selector:hover {
    cursor: pointer;
    border: 1px solid #212121;
  }
  .has-error {
    border: 1px solid #E65F50 !important;
  }
  .on-focus {
    border: 1px solid #212121 !important;
  }
  .has-item {
    padding: 2px 48px 2px 12px !important;
  }
  .disabled {
    border: 1px solid #E6E9ED !important;
    color: #BABBBE !important;
    background-color: #F1F4F8 !important;
    cursor: no-drop;
    cursor: not-allowed;
  }
  .k-multiple-selector {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
    padding: 3px 48px 3px 12px;
    border: 1px solid #D5D6DB;
    border-radius: 8px;
    background-color: #FFF;
    .items-input {
      width: 100%;
      .items {
        width: 100%;
        display: flex;
        flex-flow: wrap;
        .item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-sizing: border-box;
          padding: 6px 10px 6px 14px;
          margin-top: 4px;
          margin-bottom: 4px;
          margin-right: 8px;
          border-radius: 20px;
          color: #26BEC9;
          border: 1px solid #26BEC9;
          background-color: #F0FAFB;
          .text {
            overflow: hidden;
            text-overflow: ellipsis;
            word-wrap: break-word;
            word-break: break-all;
            text-align: left;
          }
        }
        .item-disabled {
          color: #ADE6EB !important;
          border: 1px solid #ADE6EB !important;
        }
        .no-delete {
          padding: 6px 14px !important;
        }
      }
      .filter-input {
        width: 100%;
        line-height: 40px;
        .multiple-input {
          padding: 6px 0px;
          flex: 1;
          width: 100%;
          height: 20px;
          border: none;
          outline: none;
        }
        .multiple-input:focus {
          border: none;
          outline: none;
        }
        .multiple-input::placeholder {
          color: #BABBBE;
        }
        .multiple-input::-ms-input-placeholder {
          color: #BABBBE;
        }
      }
      .select-icon {
        position: absolute;
        top: 14px;
        right: 12px;
      }
      .select-icon-active {
        transform: rotate(180deg);
      }
    }
    .items-option {
      position: absolute;
      bottom: 0px;
      width: 100%;
      left: 0px;
      z-index: 2001;
      .option {
        display: flex;
        justify-content: space-between;
        .text-label {
          padding-right: 10px;
          overflow: hidden;
          text-overflow: ellipsis;
          word-wrap: break-word;
          word-break: break-all;
          text-align: left;
        }
      }
      .selected {
        color: #068B9F;
      }
      .filter-empty {
        padding: 12px;
        color: #BABBBE;
        text-align: left;
      }
    }
    .kk-select-dropdown--auto-width {
      width: 100% !important;
      max-width: none !important;
      box-sizing: border-box !important;
      ::v-deep .kk-select-dropdown__content {
        height: fit-content;
        max-height: 240px !important;
        overflow: auto !important;
      }
    }
    .items-option-empty {
      ::v-deep .kk-select-dropdown__content {
        height: unset !important;
        max-height: 240px !important;
        overflow: auto !important;
      }
    }
    .kk-select-option:hover {
      background-color: #F0FAFB;
    }
  }
</style>
